"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem,
  SidebarHeader, SidebarFooter,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, FileText } from "lucide-react";
import { DynamicIcon } from "./icon-map";
import { useAuthStore, menuItems } from "@repo/shared";
import type { MenuItem } from "@repo/shared";
import { safeFetch } from "@/lib/safe-fetch";

const FRONTEND_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? "0.0.0";

export function AppSidebar() {
  const pathname = usePathname();
  const { hasRole } = useAuthStore();
  const [backendVersion, setBackendVersion] = useState<string | null>(null);
  const [ksefEnv, setKsefEnv] = useState<string | null>(null);
  const [companyNip, setCompanyNip] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);

  useEffect(() => {
    const fetchVersion = () => {
      safeFetch("/api/backend/version")
        .then((r) => r.json())
        .then((data) => {
          setBackendVersion(data.version);
          setKsefEnv(data.environment || null);
          setCompanyNip(data.companyNip || null);
          setCompanyName(data.companyName || null);
        })
        .catch(() => setBackendVersion(null));
    };
    fetchVersion();
    // Re-fetch when BSU or environment changes in settings
    window.addEventListener("efakt:settings-changed", fetchVersion);
    return () => window.removeEventListener("efakt:settings-changed", fetchVersion);
  }, []);

  // Display version from backend (dynamic, reads VERSION file) with frontend build-time fallback
  const displayVersion = backendVersion ?? FRONTEND_VERSION;
  const versionMismatch = backendVersion != null && backendVersion !== FRONTEND_VERSION;

  const filterMenu = (items: MenuItem[]): MenuItem[] => {
    return items
      .map((item) => {
        if (item.children) {
          const filteredChildren = item.children.filter(
            (child) => !child.requiredRole || hasRole(child.requiredRole)
          );
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        if (item.requiredRole && !hasRole(item.requiredRole)) return null;
        return item;
      })
      .filter(Boolean) as MenuItem[];
  };

  const visibleMenu = filterMenu(menuItems);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/dashboard">
                <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <FileText className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{companyName || "eFakt"}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {ksefEnv && (
                      <span className={ksefEnv === "PRD" ? "text-red-400 font-medium" : ksefEnv === "DEMO" ? "text-blue-400 font-medium" : "text-muted-foreground"}>
                        {ksefEnv}
                      </span>
                    )}
                    {ksefEnv && companyNip ? " · " : ksefEnv && !companyNip ? " · " : ""}
                    {companyNip ? (
                      <span className="font-mono">{companyNip}</span>
                    ) : ksefEnv ? (
                      <span className="text-orange-400">NIP not set</span>
                    ) : (
                      "KSeF Invoice Management"
                    )}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMenu.map((item) =>
                item.children ? (
                  <Collapsible key={item.id} defaultOpen className="group/collapsible">
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip={item.label}>
                          <DynamicIcon name={item.icon} className="size-4" />
                          <span>{item.label}</span>
                          <ChevronDown className="ml-auto size-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.children.map((child) => (
                            <SidebarMenuSubItem key={child.id}>
                              <SidebarMenuSubButton asChild isActive={pathname === child.href}>
                                <Link href={child.href || "#"} title={child.label}>
                                  <DynamicIcon name={child.icon} className="size-4" />
                                  <span>{child.label}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ) : (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton asChild isActive={pathname === item.href} tooltip={item.label}>
                      <Link href={item.href || "#"}>
                        <DynamicIcon name={item.icon} className="size-4" />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="sm"
              className="text-xs text-muted-foreground"
              tooltip={
                versionMismatch
                  ? `Version mismatch! Frontend: v${FRONTEND_VERSION}, Backend: v${backendVersion}`
                  : `eFakt v${displayVersion} — Click for changelog`
              }
              asChild
            >
              <Link href="/changelog">
                <span className={versionMismatch ? "text-destructive font-medium" : ""}>
                  v{displayVersion}
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
